import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { holidayService } from '@/api/domains/attendance';
import { holidayTypeCatalogService } from '@/api/domains/catalog/holidayType';
import type { HolidayType } from '@/api/domains/catalog/holidayType';
import type { HolidayItem } from '@/api/domains/attendance';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { formatDateToYYYYMMDD } from '@/utils';
import { editHolidaySchema, type EditHolidayFormData } from '@/schemas/attendance/editHolidayForm';


interface UseEditHolidayFormPanelParams {
  holidayData: HolidayItem;
  onSubmit?: () => void;
}

export interface UseEditHolidayFormPanelResult {
  holidayId: number;
  holidayTypeValue: string;
  holidayTypeOptions: { value: string; label: string }[];
  dateValue: string;
  reasonValue: string;
  nonWaivableValue: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  errors: ReturnType<typeof useForm<EditHolidayFormData>>['formState']['errors'];
  isSubmitted: boolean;
  handleHolidayTypeChange: (value: string | string[]) => void;
  handleDateChange: (value: string) => void;
  handleReasonChange: (value: string) => void;
  handleNonWaivableChange: (checked: boolean) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export function useEditHolidayFormPanel({
  holidayData,
  onSubmit,
}: UseEditHolidayFormPanelParams): UseEditHolidayFormPanelResult {
  const { t: translate } = useTranslation();
  const { showSuccess, showApiError, showError, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ── Local field state (pre-loaded from holidayData) ────────────────────
  const [holidayTypeValue, setHolidayTypeValue] = useState<string>(
    String(holidayData.holidayTypeId),
  );
  const [dateValue, setDateValue] = useState<string>(
    formatDateToYYYYMMDD(new Date(holidayData.date)),
  );
  const [reasonValue, setReasonValue] = useState<string>(holidayData.reason);
  const [nonWaivableValue, setNonWaivableValue] = useState<boolean>(holidayData.nonWaivable);

  // ── Catalog loading ────────────────────────────────────────────────────
  const holidayTypeCatalog = useCatalogLoader<HolidayType>(
    (signal) => holidayTypeCatalogService.getHolidayTypes(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showError(translate('attendance:holiday.messages.loadError')),
  );

  useEffect(() => {
    setLoading(!holidayTypeCatalog.isLoaded);
  }, [holidayTypeCatalog.isLoaded, setLoading]);

  // ── React Hook Form ────────────────────────────────────────────────────
  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState: { errors, isSubmitted },
  } = useForm<EditHolidayFormData>({
    resolver: zodResolver(editHolidaySchema),
    mode: 'onSubmit',
    defaultValues: {
      holidayTypeId: holidayData.holidayTypeId,
      date: formatDateToYYYYMMDD(new Date(holidayData.date)),
      reason: holidayData.reason,
      nonWaivable: holidayData.nonWaivable,
    },
  });

  // Register fields manually (local-state-driven pattern)
  useEffect(() => {
    register('holidayTypeId');
    register('date');
    register('reason');
    register('nonWaivable');
    return () => {
      unregister('holidayTypeId');
      unregister('date');
      unregister('reason');
      unregister('nonWaivable');
    };
  }, [register, unregister]);

  // Sync local state → form values
  useEffect(() => {
    setValue('holidayTypeId', holidayTypeValue ? Number(holidayTypeValue) : null);
  }, [holidayTypeValue, setValue]);
  useEffect(() => { setValue('date', dateValue); }, [dateValue, setValue]);
  useEffect(() => { setValue('reason', reasonValue); }, [reasonValue, setValue]);
  useEffect(() => { setValue('nonWaivable', nonWaivableValue); }, [nonWaivableValue, setValue]);

  // ── isReady ────────────────────────────────────────────────────────────
  const isReady = useMemo(
    () =>
      holidayTypeCatalog.isLoaded &&
      holidayTypeValue.length > 0 &&
      dateValue.length > 0 &&
      reasonValue.trim().length > 0,
    [holidayTypeCatalog.isLoaded, holidayTypeValue, dateValue, reasonValue],
  );

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleHolidayTypeChange = useCallback((value: string | string[]) => {
    setHolidayTypeValue(Array.isArray(value) ? (value[0] ?? '') : value);
  }, []);

  const handleDateChange = useCallback((value: string) => {
    setDateValue(value);
  }, []);

  const handleReasonChange = useCallback((value: string) => {
    setReasonValue(value);
  }, []);

  const handleNonWaivableChange = useCallback((checked: boolean) => {
    setNonWaivableValue(checked);
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────
  const onValidSubmit = useCallback(
    async (values: EditHolidayFormData) => {
      setIsSubmitting(true);

      try {
        const response = await holidayService.update(holidayData.id, {
          holidayTypeId: values.holidayTypeId as number,
          date: values.date,
          reason: values.reason,
          nonWaivable: values.nonWaivable,
        });

        if (!response.success) {
          if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
            injectValidationErrors(response.error.validationErrors, { setError });
            return;
          }
          showApiError(response, translate('attendance:holiday.messages.updateError'));
          return;
        }

        showSuccess(translate('attendance:holiday.messages.updateSuccess'));
        closeSidePanel();
        onSubmit?.();
      } finally {
        setIsSubmitting(false);
      }
    },
    [holidayData.id, setError, showApiError, showSuccess, closeSidePanel, onSubmit, translate],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      void rhfHandleSubmit(onValidSubmit)(event as unknown as React.BaseSyntheticEvent);
    },
    [rhfHandleSubmit, onValidSubmit],
  );

  return {
    holidayId: holidayData.id,
    holidayTypeValue,
    holidayTypeOptions: holidayTypeCatalog.options,
    dateValue,
    reasonValue,
    nonWaivableValue,
    isSubmitting,
    isReady,
    errors,
    isSubmitted,
    handleHolidayTypeChange,
    handleDateChange,
    handleReasonChange,
    handleNonWaivableChange,
    handleSubmit,
  };
}
